import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { idFromSlug, recipePath } from "@/lib/slug";
import api from "@/lib/api";
import { speak, shutUp, warmUpVoices, isIOS } from "@/lib/voice";
import { useAuth } from "@/contexts/AuthContext";
import { runRecipeVoice } from "@/lib/voiceActions";
import VoicePicker from "@/components/VoicePicker";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import useFocusTrap from "@/hooks/useFocusTrap";
import { useKitchenHelpIndex } from "@/lib/kitchenHelp";
import CookRatingPrompt from "@/components/CookRatingPrompt";

const AMBIENT_AFTER_MS = 60_000;

// ---------- Smart Timers ----------
function parseTimers(text) {
  const re = /(\d+)\s*(min(?:ute)?s?|hour|hrs?|h)\b/gi;
  const out = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    const n = parseInt(m[1], 10);
    const unit = m[2].toLowerCase();
    const seconds = /hour|hr|h/.test(unit) ? n * 3600 : n * 60;
    out.push({ label: `${n} ${unit}`, seconds });
  }
  return out;
}

// Parses voice phrases like:
//   "set timer for 10 minutes"
//   "start a 5 minute timer called pasta"
//   "10 minute timer for the bread"
//   "timer 25 min named sauce"
//   "set a five minute timer" (word-numbers up to 20)
const WORD_NUM = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20,
  "twenty-one": 21, "twenty-two": 22, "twenty-three": 23, "twenty-four": 24, "twenty-five": 25, "twenty-six": 26, "twenty-seven": 27, "twenty-eight": 28, "twenty-nine": 29,
  thirty: 30, "thirty-five": 35, forty: 40, "forty-five": 45, fifty: 50, "fifty-five": 55, sixty: 60, seventy: 70, eighty: 80, ninety: 90,
  hundred: 100,
};
function normaliseNumberWords(s) {
  let out = s;
  // Sort longest-first so "twenty-five" is caught before "twenty".
  const entries = Object.entries(WORD_NUM).sort((a, b) => b[0].length - a[0].length);
  for (const [w, n] of entries) {
    out = out.replace(new RegExp(`\\b${w}\\b`, "gi"), String(n));
  }
  return out;
}

// Turn "step three" / "step 3" into a 1-indexed integer. Returns null if not a step ref.
function parseStepNum(txt) {
  const normalised = normaliseNumberWords(txt);
  const m = normalised.match(/\bstep\s+(\d+)\b/i);
  return m ? parseInt(m[1], 10) : null;
}

function parseVoiceTimer(rawTxt) {
  const txt = normaliseNumberWords(rawTxt);
  // Common forms
  const patterns = [
    /(?:set|start|begin|put on|put)\s+(?:a|an|the)?\s*(\d+)\s*(min(?:ute)?s?|hours?|hrs?|h)\b(?:\s+timer)?(?:\s+(?:called|named|for|the)\s+(.+?))?$/i,
    /(?:set|start|begin)?\s*(?:a|an)?\s*timer\s*(?:for|of)?\s*(\d+)\s*(min(?:ute)?s?|hours?|hrs?|h)\b(?:\s+(?:called|named|for|the)\s+(.+?))?$/i,
    /(\d+)\s*(min(?:ute)?s?|hours?|hrs?|h)\s*timer(?:\s+(?:called|named|for|the)\s+(.+?))?$/i,
  ];
  for (const re of patterns) {
    const m = txt.match(re);
    if (!m) continue;
    const n = parseInt(m[1], 10);
    if (!n || isNaN(n)) continue;
    const unit = m[2].toLowerCase();
    const name = (m[3] || "").trim().replace(/^the\s+/i, "").replace(/[.?!]$/, "").slice(0, 30) || null;
    return {
      seconds: /hour|hr|h\b/.test(unit) ? n * 3600 : n * 60,
      label: `${n} ${unit}`,
      name,
    };
  }
  return null;
}

// Matches "how long left on the pasta timer", "how much time on timer", "time left on bread"
function parseTimerQuery(txt) {
  const re = /(?:how (?:much|long)|time)\s+(?:left|remaining)?\s*(?:on|for)?\s*(?:the\s+)?(.+?)(?:\s+timer)?\??$/i;
  if (!/how (?:long|much)|time left|remaining|how is/i.test(txt)) return null;
  const m = txt.match(re);
  if (!m) return { name: null };
  const name = (m[1] || "").replace(/\b(my|the|timer|all|both)\b/gi, "").trim();
  return { name: name || null };
}

function playBeep(times = 3) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    let t = ctx.currentTime;
    for (let i = 0; i < times; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.85, t + 0.02);  // was 0.25 — far louder timer alert
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      osc.start(t);
      osc.stop(t + 0.5);
      t += 0.55;
    }
    setTimeout(() => { try { ctx.close(); } catch {} }, 2500);
  } catch {}
}

function playChime() {
  // Loud confirm chime so cooks at the hob hear it over running water / sizzling
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const t = ctx.currentTime;
    // Two-note "ding ding" so it's unmistakable
    [0, 0.16].forEach((offset, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(idx === 0 ? 1320 : 1760, t + offset);
      gain.gain.setValueAtTime(0, t + offset);
      gain.gain.linearRampToValueAtTime(0.7, t + offset + 0.015);  // was 0.18 — ~4× louder
      gain.gain.exponentialRampToValueAtTime(0.0001, t + offset + 0.28);
      osc.start(t + offset);
      osc.stop(t + offset + 0.35);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 800);
  } catch {}
}

function fmt(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function spokenDuration(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m && s) return `${m} minute${m === 1 ? "" : "s"} and ${s} second${s === 1 ? "" : "s"}`;
  if (m) return `${m} minute${m === 1 ? "" : "s"}`;
  return `${s} second${s === 1 ? "" : "s"}`;
}

export default function KitchenView() {
  const { id: slugId } = useParams();
  const id = idFromSlug(slugId);
  const nav = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const { user } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const pendingAskRef = useRef(null); // follow-up question (cookbook create? which day?)
  const [step, setStep] = useState(0);
  const [ratePrompt, setRatePrompt] = useState(false);
  const [ingHelpOpen, setIngHelpOpen] = useState(false);
  const khIndex = useKitchenHelpIndex();
  // iOS Safari needs an explicit user-initiated gesture to unlock the mic
  // (autoplay-style restriction). Default voiceOn=false on iOS so the user
  // sees the "Enable voice" button and taps it themselves — which reliably
  // unlocks SpeechRecognition. Desktop/Android keeps voice-on by default.
  const [voiceOn, setVoiceOn] = useState(() => {
    if (typeof navigator === "undefined") return true;
    const ua = navigator.userAgent || "";
    const isIos = /iphone|ipad|ipod/i.test(ua);
    return !isIos;
  });
  const [voiceActuallyListening, setVoiceActuallyListening] = useState(false);
  // `voiceRestarting` is true for the brief (~100ms) window between
  // SpeechRecognition ending and the next instance starting. We surface it
  // as a subtle amber pulse on the mic icon so users see when to hold a
  // command for a beat rather than talk into a dead mic.
  const [voiceRestarting, setVoiceRestarting] = useState(false);
  // Live in-progress transcript from the SpeechRecognition interim results —
  // shown as a floating chip so the cook can SEE the mic is picking them up.
  const [liveTranscript, setLiveTranscript] = useState("");
  const [needsVoiceGesture, setNeedsVoiceGesture] = useState(false);
  // Track whether the user has already tapped the "enable voice" banner
  // ONCE. After that first tap, we never re-show the flashing banner —
  // if SR still can't start it means the browser is actually denying
  // permission and no amount of tapping will fix that. Prevents the
  // "banner keeps flashing on and off" bug reported 2026-07-07.
  const voiceGestureAckedRef = useRef(false);
  const [voiceBlocked, setVoiceBlocked] = useState(false);
  const [ttsOn, setTtsOn] = useState(true);
  const [ambient, setAmbient] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [khAnswer, setKhAnswer] = useState(null); // Cook Mode "what is…" answer card
  useEffect(() => {
    if (!khAnswer) return undefined;
    const t = setTimeout(() => setKhAnswer(null), 25000);
    return () => clearTimeout(t);
  }, [khAnswer]);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [timers, setTimers] = useState([]);
  const timersRef = useRef([]);   // mirror for synchronous voice-query reads
  const lastActivityRef = useRef(Date.now());
  const voiceOnRef = useRef(true); // synchronous mirror so command router knows the latest state
  const stepRef = useRef(0);       // sync mirror so voice "next/back/repeat" reads the latest step
  const ttsOnRef = useRef(true);  // sync mirror so voice handler reads the latest TTS toggle
  const ingHelpOpenRef = useRef(false); // sync mirror so voice handler knows if the ingredients overlay is open
  const ingScrollRef = useRef(null);    // scrollable list container inside the ingredients overlay — target for voice scroll commands
  const ingTrapRef = useFocusTrap(ingHelpOpen, () => setIngHelpOpen(false));

  // Keep refs in sync with state
  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);
  const [silentTip, setSilentTip] = useState(false);
  const dismissSilentTip = () => { setSilentTip(false); try { sessionStorage.setItem("rf_silent_tip", "1"); } catch { /* ignore */ } };
  useEffect(() => {
    if (!voiceOn || !isIOS()) return;
    try { if (sessionStorage.getItem("rf_silent_tip") === "1") return; } catch { /* ignore */ }
    setSilentTip(true);
    const t = setTimeout(dismissSilentTip, 12000);
    return () => clearTimeout(t);
  }, [voiceOn]);
  useEffect(() => { timersRef.current = timers; }, [timers]);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { ttsOnRef.current = ttsOn; }, [ttsOn]);
  useEffect(() => { ingHelpOpenRef.current = ingHelpOpen; }, [ingHelpOpen]);

  useEffect(() => {
    api.get(`/recipes/${id}`).then((r) => setRecipe(r.data));
  }, [id]);

  useEffect(() => { warmUpVoices(); }, []);

  // Online/offline tracking for the small "Offline — cached recipe" badge
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // First-visit auto-open of the Help overlay so new cooks discover voice commands
  useEffect(() => {
    try {
      if (!localStorage.getItem("rf_kitchen_help_seen")) {
        setHelpOpen(true);
        localStorage.setItem("rf_kitchen_help_seen", "1");
      }
    } catch {}
  }, []);

  // Escape key closes the Help overlay (helps when the close X scrolls below the fold)
  useEffect(() => {
    if (!helpOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setHelpOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen]);

  // Wake lock
  useEffect(() => {
    let wl = null;
    if ("wakeLock" in navigator) {
      navigator.wakeLock.request("screen").then((w) => { wl = w; }).catch(() => {});
    }
    return () => { try { wl?.release(); } catch {} };
  }, []);

  // Ambient idle timer
  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); if (ambient) setAmbient(false); };
    window.addEventListener("keydown", bump);
    window.addEventListener("touchstart", bump, { passive: true });
    window.addEventListener("mousemove", bump);
    const iv = setInterval(() => {
      if (!ambient && !helpOpen && Date.now() - lastActivityRef.current > AMBIENT_AFTER_MS) setAmbient(true);
    }, 2000);
    return () => {
      window.removeEventListener("keydown", bump);
      window.removeEventListener("touchstart", bump);
      window.removeEventListener("mousemove", bump);
      clearInterval(iv);
    };
  }, [ambient, helpOpen]);

  // Timer tick — beep + speak when remaining hits 0. Paused timers are skipped.
  useEffect(() => {
    const iv = setInterval(() => {
      setTimers((arr) => arr.map((t) => {
        if (t.paused) return t;
        if (t.remaining > 0) {
          const r = t.remaining - 1;
          if (r === 0 && !t.alerted) {
            playBeep(3);
            try { navigator.vibrate?.([300, 120, 300, 120, 300]); } catch {}
            const subject = t.name ? `the ${t.name} timer` : `your ${t.label} timer`;
            const stepSuffix = Number.isInteger(t.stepIndex) ? ` from step ${t.stepIndex + 1}` : "";
            speak(`${subject}${stepSuffix} is done.`);
            return { ...t, remaining: 0, alerted: true };
          }
          return { ...t, remaining: r };
        }
        return t;
      }));
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  const launchTimer = (label, seconds, name = null, stepIndex = null) => {
    const tid = `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setTimers((arr) => [
      ...arr,
      { id: tid, label, name, total: seconds, remaining: seconds, alerted: false, paused: false, stepIndex },
    ]);
    playChime();
    const subject = name ? `Timer named ${name}, set for ${spokenDuration(seconds)}.` : `Timer started for ${spokenDuration(seconds)}.`;
    speak(subject);
  };

  const removeTimer = (tid) => setTimers((arr) => arr.filter((t) => t.id !== tid));

  // Voice "stop/cancel timer" — cancels all timers (no step number given) OR
  // just the timers attached to a specific step (1-indexed for the spoken UI).
  const cancelTimer = (stepNum1Indexed = null) => {
    setTimers((arr) => {
      if (stepNum1Indexed == null) {
        if (arr.length === 0) { speak("You don't have any running timers."); return arr; }
        speak(`Cancelled ${arr.length === 1 ? "the timer" : `all ${arr.length} timers`}.`);
        return [];
      }
      const target = stepNum1Indexed - 1;
      const matching = arr.filter((t) => t.stepIndex === target);
      if (matching.length === 0) {
        speak(`No timer running for step ${stepNum1Indexed}.`);
        return arr;
      }
      speak(`Cancelled step ${stepNum1Indexed} timer.`);
      return arr.filter((t) => t.stepIndex !== target);
    });
  };

  // Voice "change/set timer to X minutes" — replaces existing timer(s) for the
  // given step with a fresh one of the new length. If no timer exists for that
  // step, creates one.
  const changeTimer = (stepNum1Indexed, newSeconds) => {
    const target = stepNum1Indexed - 1;
    setTimers((arr) => {
      const without = arr.filter((t) => t.stepIndex !== target);
      const tid = `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      return [
        ...without,
        { id: tid, label: spokenDuration(newSeconds), name: null, total: newSeconds, remaining: newSeconds, alerted: false, paused: false, stepIndex: target },
      ];
    });
    speak(`Step ${stepNum1Indexed} timer set to ${spokenDuration(newSeconds)}.`);
  };

  // Voice "pause timer" / "pause step 3 timer" — pauses countdown but keeps chip visible.
  // With no step number given, pauses ALL running timers.
  const pauseTimer = (stepNum1Indexed = null) => {
    setTimers((arr) => {
      if (arr.length === 0) { speak("You don't have any running timers."); return arr; }
      if (stepNum1Indexed == null) {
        const running = arr.filter((t) => !t.paused && t.remaining > 0);
        if (running.length === 0) { speak("All timers are already paused."); return arr; }
        speak(`Paused ${running.length === 1 ? "the timer" : `all ${running.length} timers`}.`);
        return arr.map((t) => (t.remaining > 0 ? { ...t, paused: true } : t));
      }
      const target = stepNum1Indexed - 1;
      const matching = arr.filter((t) => t.stepIndex === target);
      if (matching.length === 0) { speak(`No timer running for step ${stepNum1Indexed}.`); return arr; }
      speak(`Paused step ${stepNum1Indexed} timer.`);
      return arr.map((t) => (t.stepIndex === target && t.remaining > 0 ? { ...t, paused: true } : t));
    });
  };

  // Voice "resume timer" / "restart step 3 timer" — un-pauses.
  const resumeTimer = (stepNum1Indexed = null) => {
    setTimers((arr) => {
      if (arr.length === 0) { speak("You don't have any timers."); return arr; }
      if (stepNum1Indexed == null) {
        const paused = arr.filter((t) => t.paused);
        if (paused.length === 0) { speak("No timers are paused."); return arr; }
        speak(`Resumed ${paused.length === 1 ? "the timer" : `all ${paused.length} timers`}.`);
        return arr.map((t) => (t.paused ? { ...t, paused: false } : t));
      }
      const target = stepNum1Indexed - 1;
      const matching = arr.filter((t) => t.stepIndex === target && t.paused);
      if (matching.length === 0) { speak(`No paused timer for step ${stepNum1Indexed}.`); return arr; }
      speak(`Resumed step ${stepNum1Indexed} timer.`);
      return arr.map((t) => (t.stepIndex === target ? { ...t, paused: false } : t));
    });
  };

  // Speak a query response about timers
  const announceTimerStatus = (query) => {
    const arr = timersRef.current.filter((t) => t.remaining > 0);
    if (arr.length === 0) { speak("You have no running timers."); return; }
    let match = arr;
    if (query?.name) {
      const lower = query.name.toLowerCase();
      const filt = arr.filter((t) => (t.name || t.label).toLowerCase().includes(lower));
      if (filt.length) match = filt;
    }
    if (match.length === 1) {
      const t = match[0];
      const subject = t.name ? `the ${t.name} timer` : `your ${t.label} timer`;
      speak(`${subject} has ${spokenDuration(t.remaining)} left.`);
    } else {
      const parts = match.slice(0, 4).map((t) => {
        const s = t.name ? `the ${t.name} timer` : `the ${t.label} timer`;
        return `${s} ${spokenDuration(t.remaining)}`;
      });
      speak(`Remaining — ${parts.join("; ")}.`);
    }
  };

  // Command router — turns a recognised transcript into an action.
  // Returns `true` if the transcript matched a command (used by the voice hook
  // to de-dupe interim→final echoes so the same "next" doesn't fire twice).
  const handleCommand = useCallback((rawTxt) => {
    if (!voiceOnRef.current) return false; // race-condition guard
    if (!recipe) return false;
    const raw = rawTxt.trim();
    const txt = normaliseNumberWords(raw); // "three" → "3" etc — critical for step-num commands
    lastActivityRef.current = Date.now();
    // ANY recognised utterance wakes ambient mode.
    setAmbient(false);

    // Voice off / exit — HIGHEST priority so we tear down before any other branch
    if (pendingAskRef.current) {
      const fn = pendingAskRef.current;
      pendingAskRef.current = null;
      const res = fn(raw.toLowerCase().replace(/[.?!]+$/, ""));
      if (res === "retry") { pendingAskRef.current = fn; return true; }
      if (res) return true;
    }
    if (runRecipeVoice(raw, {
      recipe, user: userRef.current, speak,
      ask: (fn) => { pendingAskRef.current = fn; },
    })) {
      playChime();
      return true;
    }
    if (/\b(voice off|mute|stop listening|stop the voice|turn off voice)\b/i.test(txt)) {
      playChime();
      speak("Voice turned off.");
      setVoiceOn(false);
      setTimeout(killVoice, 200);
      return true;
    }
    if (/\b(exit|close kitchen|leave kitchen|leave the kitchen|stop cooking)\b/i.test(txt)) {
      killVoice();
      nav(recipePath(recipe || id, recipe?.title));
      return true;
    }

    // ---------- Timer PAUSE / RESUME — MUST come before generic "stop" cancel ----------
    const pauseMatch = txt.match(/\b(?:please\s+)?(?:pause|hold|freeze)\s+(?:the\s+)?(?:step\s+(\d+)\s+)?(?:timer|timers?)\b/i);
    if (pauseMatch) {
      playChime();
      pauseTimer(pauseMatch[1] ? parseInt(pauseMatch[1], 10) : null);
      return true;
    }
    const resumeMatch = txt.match(/\b(?:please\s+)?(?:resume|un ?pause|continue)\s+(?:the\s+)?(?:step\s+(\d+)\s+)?(?:timer|timers?)\b/i);
    if (resumeMatch) {
      playChime();
      resumeTimer(resumeMatch[1] ? parseInt(resumeMatch[1], 10) : null);
      return true;
    }

    // ---------- Timer CANCEL/CLEAR — MUST come before timer creation ----------
    const cancelMatch = txt.match(/\b(?:please\s+)?(?:stop|cancel|clear|kill|end|remove|delete)\s+(?:the\s+)?(?:step\s+(\d+)\s+)?(?:timer|timers?\s+all|all\s+timers)/i);
    if (cancelMatch) {
      playChime();
      const stepNum = cancelMatch[1] ? parseInt(cancelMatch[1], 10) : null;
      cancelTimer(stepNum);
      return true;
    }

    // ---------- Timer CHANGE/UPDATE length ----------
    const changeMatch = txt.match(/\b(?:change|update|reset)\s+(?:the\s+)?(?:step\s+(\d+)\s+)?timer\s+to\s+(\d+)\s*(minute|minutes|min|m|second|seconds|sec|s)\b/i);
    if (changeMatch) {
      playChime();
      const stepNum = changeMatch[1] ? parseInt(changeMatch[1], 10) : stepRef.current + 1;
      const amount = parseInt(changeMatch[2], 10);
      const unit = changeMatch[3].toLowerCase();
      const seconds = unit.startsWith("s") ? amount : amount * 60;
      changeTimer(stepNum, seconds);
      return true;
    }

    // Timer creation
    const tCreate = parseVoiceTimer(txt);
    if (tCreate) { playChime(); launchTimer(tCreate.label, tCreate.seconds, tCreate.name, stepRef.current); return true; }

    // Timer query
    if (/\b(?:how (?:long|much)|time left|time remaining|how much time)\b/i.test(txt)) {
      playChime();
      const q = parseTimerQuery(txt);
      announceTimerStatus(q);
      return true;
    }

    // Help toggle
    if (/\b(?:close help|hide help|dismiss help)\b/i.test(txt)) {
      setHelpOpen(false);
      playChime();
      return true;
    }
    // Ingredients overlay — open phrasings (show / open / display / list /
    // "show me" / "what are" / "ingredients please" / short "ingredients"
    // if there's no verb and the overlay is closed). Kept BEFORE "open
    // help" so "open ingredients" doesn't also match a stray "open" rule
    // later in the pipeline.
    if (
      /\bingredients?\s+(?:on|open|up|please|list)\b/i.test(txt) ||
      /\b(?:show|open|display|bring\s+up|pull\s+up|list|read(?:\s+out)?)\s+(?:me\s+)?(?:the\s+)?ingredients?(?:\s+list)?\b/i.test(txt) ||
      /\b(?:give|read)\s+me\s+(?:the\s+)?ingredients?(?:\s+list)?\b/i.test(txt) ||
      /\bwhat(?:'s|\s+are)?\s+(?:the\s+)?ingredients?\b/i.test(txt) ||
      /\bingredients?\s+list\b/i.test(txt)
    ) {
      playChime();
      setIngHelpOpen(true);
      speak("Ingredients open.");
      return true;
    }
    // Ingredients overlay — close phrasings. Rich with natural utterances
    // because cooks lean on short words ("close", "done", "dismiss") once
    // the panel is already up. When the overlay IS open, short close words
    // alone are treated as unambiguous — context removes the risk.
    const closeIngExplicit =
      /\bingredients?\s+(?:off|closed?|down|away|dismiss(?:ed)?)\b/i.test(txt) ||
      /\b(?:close|shut|hide|dismiss|turn\s+off|take\s+away|put\s+away|get\s+rid\s+of)\s+(?:the\s+)?(?:ingredients?|ingredient\s+list|list|popup|overlay|panel)\b/i.test(txt) ||
      /\bdone\s+(?:with\s+)?(?:the\s+)?ingredients?\b/i.test(txt) ||
      /\bback\s+to\s+cook(?:ing)?\b/i.test(txt);
    const closeIngShortWhileOpen =
      ingHelpOpenRef.current && /^(?:close|dismiss|done|hide(?:\s+it)?|back|exit\s+ingredients?|out|cancel|clear)\s*(?:it|that|this|the\s+popup|the\s+overlay|the\s+panel|the\s+list)?\s*\.?$/i.test(txt.trim());
    if (closeIngExplicit || closeIngShortWhileOpen) {
      playChime();
      setIngHelpOpen(false);
      speak("Ingredients closed.");
      return true;
    }
    // Ingredient-overlay scroll commands — only honoured while the panel
    // is open so cooks can walk a long ingredients list hands-free. Guarded
    // by `ingHelpOpenRef` so "next"/"back" keep driving step navigation
    // when the panel is closed. Smooth-scroll by ~70% of viewport height so
    // one command moves roughly one screenful without losing context.
    if (ingHelpOpenRef.current) {
      const scroller = ingScrollRef.current;
      // "top of list" / "scroll to top" / "start of list" / "jump to top"
      if (/\b(?:scroll\s+to\s+top|jump\s+to\s+top|top\s+of\s+(?:the\s+)?(?:list|ingredients?)|back\s+to\s+top|go\s+to\s+(?:the\s+)?top|start\s+of\s+(?:the\s+)?list)\b/i.test(txt)) {
        if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" });
        playChime();
        return true;
      }
      // "scroll to bottom" / "end of list" / "jump to bottom"
      if (/\b(?:scroll\s+to\s+bottom|jump\s+to\s+bottom|bottom\s+of\s+(?:the\s+)?(?:list|ingredients?)|end\s+of\s+(?:the\s+)?list|go\s+to\s+(?:the\s+)?(?:bottom|end))\b/i.test(txt)) {
        if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
        playChime();
        return true;
      }
      // "scroll down" / "down" / "more" / "keep going" / "next page" / "page down" /
      // "next ingredient(s)" / "show more" — walk the list forward
      if (/\b(?:scroll\s+down|page\s+down|down\s+(?:a\s+bit|please|some|more)?|keep\s+(?:going|scrolling)|show\s+more|more\s+(?:please|ingredients?)?|next\s+(?:page|ingredients?|few)|further\s+down)\b/i.test(txt)) {
        if (scroller) {
          const amount = Math.max(120, scroller.clientHeight * 0.7);
          scroller.scrollBy({ top: amount, behavior: "smooth" });
        }
        playChime();
        return true;
      }
      // "scroll up" / "up" / "previous ingredient(s)" / "page up" / "back up" /
      // "go up" — walk the list backward
      if (/\b(?:scroll\s+up|page\s+up|up\s+(?:a\s+bit|please|some|more)?|back\s+up|go\s+up|further\s+up|previous\s+(?:page|ingredients?|few))\b/i.test(txt)) {
        if (scroller) {
          const amount = Math.max(120, scroller.clientHeight * 0.7);
          scroller.scrollBy({ top: -amount, behavior: "smooth" });
        }
        playChime();
        return true;
      }
    }
    // "what is arrowroot?" — answer from Kitchen Help without leaving the recipe.
    const askMatch = raw.match(/^(?:hey\s+)?(?:what\s+(?:is|are|does)|what'?s|whats|define|explain|meaning\s+of|tell\s+me\s+about)\s+(?:an?\s+|the\s+)?(.+?)(?:\s+mean)?[?.!]*$/i);
    if (askMatch && !/\b(?:next|previous|step|time|timers?|left|ingredients?|this|that|it|on|happening)\b/i.test(askMatch[1])) {
      playChime();
      const subject = askMatch[1];
      api.get("/kitchen-help/ask", { params: { q: raw } })
        .then(({ data }) => {
          if (data?.answer) {
            setKhAnswer(data.answer);
            speak(`${data.answer.term}: ${data.answer.definition}`);
          } else {
            setKhAnswer({ term: subject, definition: null });
            speak(`Sorry, ${subject} isn't in Kitchen Help yet. I've let the team know.`);
          }
        })
        .catch(() => speak("Sorry, I couldn't look that up just now."));
      return true;
    }
    if (/\b(?:close answer|hide answer|dismiss answer)\b/i.test(txt)) {
      setKhAnswer(null);
      playChime();
      return true;
    }
    if (/\b(?:^help$|open help|show help|how do i use this|what can i say)\b/i.test(txt)) {
      setHelpOpen(true);
      playChime();
      return true;
    }

    // Read toggle (TTS)
    if (/\b(?:read off|stop reading|reading off|silence|quiet)\b/i.test(txt)) {
      setTtsOn(false);
      shutUp();
      playChime();
      return true;
    }
    if (/\b(?:read on|start reading|reading on|read the steps?|read aloud)\b/i.test(txt)) {
      setTtsOn(true);
      playChime();
      speak("Reading turned on.");
      return true;
    }

    // ---------- Jump to specific step ----------
    const jumpMatch = txt.match(/\b(?:(?:go\s+(?:back|forward|straight)?\s*)?(?:to\s+)?|jump\s+to\s+|skip\s+to\s+|open\s+)?step\s+(\d+)\b/i)
      || txt.match(/\b(?:go|jump|skip|move)\s+(?:back|forward|straight)?\s*(?:to\s+)?step\s+(\d+)\b/i);
    if (jumpMatch) {
      const targetStep1 = parseInt(jumpMatch[1], 10);
      if (!Number.isNaN(targetStep1) && targetStep1 >= 1 && targetStep1 <= recipe.instructions.length) {
        playChime();
        const target0 = targetStep1 - 1;
        setStep(target0);
        if (ttsOnRef.current) speak(recipe.instructions[target0] || "");
        else speak(`Step ${targetStep1}.`);
        return true;
      }
      if (!Number.isNaN(targetStep1)) {
        speak(`This recipe only has ${recipe.instructions.length} steps.`);
        return true;
      }
    }

    // ---------- Navigation ----------
    const BACK_RE = /\b(?:back|go back|previous|previous step|last step|prior step|step back|move back|reverse|backwards?|go to (?:the )?(?:previous|last) step)\b/i;
    const NEXT_RE = /\b(?:next|next step|forward|continue|go on|carry on|onward|onwards|move on|proceed|advance|step forward|skip ahead)\b/i;
    const REPEAT_RE = /\b(?:repeat|repeat that|repeat step|read it again|read again|say again|say it again|once more|come again|what was that)\b/i;

    if (BACK_RE.test(txt)) {
      playChime();
      setStep((i) => {
        const prev = Math.max(i - 1, 0);
        if (ttsOnRef.current) speak(recipe.instructions[prev] || "");
        return prev;
      });
      return true;
    } else if (NEXT_RE.test(txt)) {
      playChime();
      setStep((i) => {
        const next = Math.min(i + 1, recipe.instructions.length - 1);
        if (ttsOnRef.current) speak(recipe.instructions[next] || "");
        return next;
      });
      return true;
    } else if (REPEAT_RE.test(txt)) {
      playChime();
      speak(recipe.instructions[stepRef.current] || "");
      return true;
    }
    return false;
    // Anything else is treated as ambient kitchen noise — ignored.
  }, [recipe, id, nav]);
  useEffect(() => { window.__rfKitchenCommand = handleCommand; }, [handleCommand]); // QA hook

  // Hook up voice recognition. The hook internally handles iOS quirks
  // (interim-result matching to collapse the 15s delay, auto-restart on
  // onend, InvalidStateError retries, and getUserMedia priming).
  const { kill: killRecognition, pushToTalk, armOnce } = useVoiceRecognition({
    enabled: voiceOn,
    ready: !!recipe,
    onCommand: handleCommand,
    onInterim: setLiveTranscript,
    onListening: (b) => { setVoiceActuallyListening(b); if (b) setNeedsVoiceGesture(false); },
    onRestarting: setVoiceRestarting,
    onNeedsGesture: () => setNeedsVoiceGesture(true),
    onBlocked: () => setVoiceBlocked(true),
    gestureAckedRef: voiceGestureAckedRef,
  });

  // Hard-stop helper: tears down the active SpeechRecognition AND cancels any in-flight TTS.
  // Called whenever the user says "voice off" / "exit", clicks the voice toggle, or unmounts.
  const killVoice = () => {
    voiceOnRef.current = false;
    killRecognition();
    shutUp(); // cancel any TTS that's mid-sentence
  };

  // Belt-and-braces: when the component unmounts (user navigates away), kill voice & TTS
  useEffect(() => () => { killVoice(); }, []);

  // Auto-speak step when TTS toggled on and step changes. Step one is read the moment Kitchen View opens.
  const firstReadRef = useRef(false);
  const [tapToHear, setTapToHear] = useState(false);
  useEffect(() => {
    const text = recipe?.instructions?.[step];
    if (!ttsOn || !text) return undefined;
    if (firstReadRef.current) { speak(text); return undefined; }
    const n = recipe.instructions.length;
    const intro = `${recipe.title ? `${recipe.title}. ` : ""}Step 1 of ${n}. ${text}`;
    // Browsers block speech until the page has had a tap; if so, read on the first tap anywhere.
    if (navigator.userActivation && !navigator.userActivation.hasBeenActive) {
      setTapToHear(true);
      const go = () => { setTapToHear(false); firstReadRef.current = true; speak(stepRef.current === 0 ? intro : recipe.instructions[stepRef.current]); };
      window.addEventListener("pointerdown", go, { once: true });
      return () => window.removeEventListener("pointerdown", go);
    }
    // Mark as read only when the timer actually fires — a re-run inside 400ms (StrictMode double fetch,
    // recipe refetch) clears the timer and must not lose the "Title. Step 1 of N." intro.
    const t = setTimeout(() => { firstReadRef.current = true; speak(step === 0 ? intro : text); }, 400);
    return () => clearTimeout(t);
  }, [step, ttsOn, recipe]);

  const currentStepText = recipe?.instructions?.[step] || "";
  const stepTimers = useMemo(() => parseTimers(currentStepText), [currentStepText]);

  if (!recipe) {
    return <div className="kitchen-screen min-h-screen flex items-center justify-center"><p>Loading…</p></div>;
  }
  const total = recipe.instructions.length;
  const exitToRecipe = () => { killVoice(); nav(recipePath(recipe || id, recipe?.title)); };
  const finishCooking = async () => {
    if (!user || !recipe || recipe.owner_id === user.user_id || recipe.is_historical) { exitToRecipe(); return; }
    try {
      const { data } = await api.get(`/recipes/${recipe.recipe_id}/my-rating`);
      if (data?.stars) { exitToRecipe(); return; }
    } catch { /* still ask */ }
    killVoice();
    setRatePrompt(true);
  };

  return (
    <div
      className="kitchen-screen min-h-screen flex flex-col relative"
      onClick={(e) => {
        lastActivityRef.current = Date.now();
        // First click anywhere on Kitchen View counts as the user gesture
        // that browsers require to start microphone-bound APIs. If voice
        // was blocked on mount, use THIS click as the gesture and try
        // again immediately (calling buildAndStart directly, not via a
        // voiceOn toggle race). Then bail out early — the tap consumed
        // the gesture and must NOT also advance the recipe step, else
        // enabling voice looks like the recipe skipped forward on its
        // own. This was the observed "flashing banner + skips step" bug.
        if (needsVoiceGesture && voiceOn) {
          setNeedsVoiceGesture(false);
          voiceGestureAckedRef.current = true;
          try {
            const wasOn = voiceOnRef.current;
            voiceOnRef.current = false;
            setVoiceOn(false);
            requestAnimationFrame(() => {
              voiceOnRef.current = wasOn;
              setVoiceOn(true);
            });
          } catch { /* silent */ }
          return; // <-- do NOT advance step on this tap
        }
        if (ambient) { setAmbient(false); return; }
        if (helpOpen || ingHelpOpen) return;
        const x = e.clientX;
        const w = window.innerWidth;
        if (x > w * 0.55) setStep((i) => Math.min(i + 1, total - 1));
        else if (x < w * 0.45) setStep((i) => Math.max(i - 1, 0));
      }}
    >
      {/* Tap-to-enable-voice overlay — shown ONCE when the browser blocks
          SR auto-start and voice is enabled. After the user taps, we
          latch `voiceGestureAckedRef` so this banner never reappears in
          this session (prevents the "flashes on and off" bug). If SR
          still fails after that first tap, `voiceBlocked` renders the
          persistent alternative chip below. */}
      {(needsVoiceGesture && voiceOn && !voiceBlocked) && (
        <div
          data-testid="kitchen-voice-gesture-prompt"
          className="absolute inset-x-0 top-20 z-30 flex justify-center px-4 pointer-events-none"
        >
          <div className="bg-rf-amber text-rf-ink rounded-full px-5 py-2.5 text-sm font-extrabold shadow-2xl animate-pulse">
            🎤 Tap anywhere to enable voice control
          </div>
        </div>
      )}
      {/* Persistent voice-blocked chip. Non-pulsing so it doesn't nag —
          just tells the cook the mic is denied and where to fix it. */}
      {voiceBlocked && voiceOn && (
        <div
          data-testid="kitchen-voice-blocked"
          className="absolute inset-x-0 top-20 z-30 flex justify-center px-4 pointer-events-none"
        >
          <div className="bg-red-100 border-2 border-red-500 text-red-800 rounded-2xl px-4 py-2 text-xs font-bold shadow-2xl max-w-sm text-center pointer-events-auto">
            🎤 Microphone blocked. Enable it in your browser settings, then tap the <b>Voice</b> button in the top bar.
          </div>
        </div>
      )}
      {/* iOS first-load prompt — voice starts OFF on iPhone/iPad because
          Apple requires an explicit user gesture to unlock the microphone.
          The chip nudges the cook to tap "Voice off" in the top bar.
          Hidden after the user has interacted so it never flashes. */}
      {!voiceOn && !voiceGestureAckedRef.current && (typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent || "")) && (
        <div
          data-testid="kitchen-voice-ios-hint"
          className="absolute inset-x-0 top-20 z-30 flex justify-center px-4 pointer-events-none"
        >
          <button
            type="button"
            data-testid="kitchen-voice-ios-enable"
            className="bg-rf-amber text-rf-ink rounded-full px-5 py-2.5 text-sm font-extrabold shadow-2xl animate-pulse pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation();
              voiceGestureAckedRef.current = true;
              setVoiceOn(true);
              setNeedsVoiceGesture(false);
            }}
          >
            🎤 Tap to turn on Voice Listening
          </button>
        </div>
      )}
      {voiceOn && silentTip && (
        <div data-testid="kitchen-silent-mode-tip" role="note" className="absolute inset-x-0 top-36 z-30 flex justify-center px-4 pointer-events-none">
          <div className="bg-rf-ink/95 border border-rf-amber/60 text-rf-cream rounded-2xl pl-4 pr-2 py-2 text-xs font-bold shadow-2xl max-w-sm flex items-center gap-2 pointer-events-auto">
            <span>On iPhone? Turn off <span className="text-rf-amber">Silent mode</span> and turn the volume up to hear the steps read aloud.</span>
            <button type="button" data-testid="kitchen-silent-mode-tip-dismiss" aria-label="Dismiss tip" onClick={(e) => { e.stopPropagation(); dismissSilentTip(); }} className="shrink-0 rounded-full px-2 py-1 text-rf-amber hover:bg-rf-cream/10">OK</button>
          </div>
        </div>
      )}
      {/* Push-to-talk fallback — appears when the auto-restart loop has
          silently failed 3× (iOS Safari sometimes rejects .start() without
          throwing; watchdog in useVoiceRecognition trips this after too
          many empty cycles). Big single-tap button gives the cook a
          reliable way to fire ONE command per tap, no matter what iOS
          decides to do with the audio session. */}
      {pushToTalk && voiceOn && (
        <div
          data-testid="kitchen-voice-ptt"
          className="absolute inset-x-0 top-20 z-30 flex justify-center px-4 pointer-events-none"
        >
          <button
            type="button"
            data-testid="kitchen-voice-ptt-btn"
            onClick={(e) => { e.stopPropagation(); armOnce(); }}
            className="bg-rf-orange text-white rounded-full px-6 py-3 text-sm font-black shadow-2xl pointer-events-auto inline-flex items-center gap-2 hover:bg-rf-ink"
          >
            <i className="fa-solid fa-microphone" aria-hidden="true" />
            {voiceActuallyListening ? "Listening… speak now" : "Tap & speak your command"}
          </button>
        </div>
      )}
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 sm:p-6 text-rf-cream/70 text-sm no-print gap-2">
        <button data-testid="kitchen-exit" onClick={(e) => { e.stopPropagation(); exitToRecipe(); }} className="font-bold hover:text-rf-amber">← Exit</button>
        <div className="flex items-center gap-3">
          {offline && (
            <span
              data-testid="kitchen-offline-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rf-amber/20 text-rf-amber font-bold text-xs"
              title="No internet — viewing this recipe from the offline cache"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rf-amber animate-pulse" />
              Offline
            </span>
          )}
          <div className="font-bold">{step + 1} / {total}</div>
          {tapToHear && ttsOn && (
            <span data-testid="kitchen-tap-to-hear" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rf-amber text-rf-ink font-bold text-xs animate-pulse">
              Tap anywhere to hear step 1
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <button data-testid="kitchen-help" onClick={(e) => { e.stopPropagation(); setHelpOpen(true); }} className="font-bold text-rf-cream/70 hover:text-rf-amber" aria-label="How to use Kitchen View">
            ? Help
          </button>
          <button
            data-testid="kitchen-ing-help"
            onClick={(e) => { e.stopPropagation(); setIngHelpOpen(true); }}
            className="font-bold text-rf-cream/70 hover:text-rf-amber"
            aria-label="Ingredient help"
            title="Look up an ingredient"
          >
            ? Ingredients
          </button>
          <button data-testid="kitchen-tts" onClick={(e) => { e.stopPropagation(); const next = !ttsOn; if (!next) shutUp(); setTtsOn(next); }} className={`font-bold ${ttsOn ? "text-rf-amber" : "text-rf-cream/40"}`}>
            {ttsOn ? "Read ON" : "Read OFF"}
          </button>
          <button data-testid="kitchen-voice" onClick={(e) => {
            e.stopPropagation();
            const next = !voiceOn;
            if (!next) killVoice();
            // Explicit user tap on the Voice toggle resets the gesture
            // and blocked state so the user gets a fresh attempt (useful
            // after they've enabled the mic in browser settings).
            voiceGestureAckedRef.current = false;
            setVoiceOn(next);
            setNeedsVoiceGesture(false);
            setVoiceBlocked(false);
          }} className={`font-bold inline-flex items-center gap-1.5 ${voiceOn ? (voiceActuallyListening ? "text-rf-amber" : voiceBlocked ? "text-red-400" : "text-rf-amber/60") : "text-rf-cream/40"}`}>
            {/* Restart pulse — amber dot appears during the ~100ms gap
                between SR instances so the cook knows to hold a beat. */}
            {voiceOn && voiceRestarting && (
              <span
                data-testid="kitchen-voice-restarting"
                aria-label="Voice recogniser restarting"
                className="inline-block w-2 h-2 rounded-full bg-rf-amber animate-pulse"
              />
            )}
            {voiceOn ? (voiceActuallyListening ? "Voice listening" : voiceBlocked ? "Voice blocked" : voiceRestarting ? "Voice restarting…" : (needsVoiceGesture ? "Voice — tap to enable" : "Voice on…")) : "Voice off"}
          </button>
        </div>
      </div>

      {/* Live transcript chip — shows what the mic is currently hearing so
          the cook knows commands are being picked up. Empty in silence.
          Pinned above every overlay (z-[60]) so it stays visible even when
          the ingredients panel is open — otherwise cooks can't tell
          whether voice "close ingredients" is being heard. */}
      {voiceOn && voiceActuallyListening && liveTranscript && (
        <div
          data-testid="kitchen-live-transcript"
          className="fixed left-1/2 -translate-x-1/2 top-16 z-[60] max-w-[85vw] px-4 py-2 rounded-full bg-rf-ink/85 backdrop-blur text-rf-amber text-sm font-medium tabular-nums shadow-lg pointer-events-none"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-rf-amber animate-pulse mr-2 align-middle" />
          &ldquo;{liveTranscript}&rdquo;
        </div>
      )}

      {/* Title */}
      <div className="px-6 sm:px-8 pt-2">
        <p className="text-rf-amber overline">{recipe.title}</p>
      </div>

      {/* Main step */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 md:px-16 text-center">
        <p
          data-testid="kitchen-step-label"
          className="font-display font-black uppercase tracking-[0.2em] text-rf-amber text-base sm:text-lg md:text-xl mb-6 sm:mb-8"
        >
          Step <span className="text-rf-cream">{step + 1}</span> of {total}
        </p>
        <p className="kitchen-text text-3xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1.1] text-rf-cream max-w-5xl">
          {currentStepText}
        </p>
        {stepTimers.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-3 justify-center no-print">
            {stepTimers.map((t, i) => (
              <button
                key={i}
                data-testid={`step-timer-${i}`}
                onClick={(e) => { e.stopPropagation(); launchTimer(t.label, t.seconds, null, step); }}
                className="px-5 py-2.5 rounded-full bg-rf-amber text-rf-ink font-bold text-sm sm:text-base hover:scale-105 transition-transform"
              >
                ⏱ Start {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-between p-4 sm:p-6 no-print">
        <button data-testid="kitchen-back" onClick={(e) => { e.stopPropagation(); setStep((i) => Math.max(i - 1, 0)); }} disabled={step === 0} className="btn-outline-ink !border-rf-cream/40 !text-rf-cream disabled:opacity-30">← Back</button>
        <div className="flex gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full ${i === step ? "bg-rf-amber" : "bg-rf-cream/20"}`} />
          ))}
        </div>
        <button data-testid={step === total - 1 ? "kitchen-finish" : "kitchen-next"} onClick={(e) => { e.stopPropagation(); if (step === total - 1) finishCooking(); else setStep((i) => Math.min(i + 1, total - 1)); }} className="btn-amber">{step === total - 1 ? "I'm done!" : "Next →"}</button>
      </div>
      {ratePrompt && <CookRatingPrompt recipe={recipe} onDone={exitToRecipe} />}

      {/* Floating timer tray — pinned top-centre so they're always in view while cooking */}
      {timers.length > 0 && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 flex flex-wrap gap-2 sm:gap-3 z-30 no-print justify-center max-w-[95vw]"
          data-testid="timer-tray"
        >
          {timers.map((t) => {
            const done = t.remaining === 0;
            const paused = !!t.paused && !done;
            return (
              <div
                key={t.id}
                data-testid={`timer-chip-${t.id}`}
                onClick={(e) => e.stopPropagation()}
                className={`flex items-center gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl shadow-2xl border-2 ${done ? "bg-rf-orange text-white border-rf-orange animate-pulse" : paused ? "bg-rf-cream/70 text-rf-ink border-rf-ink/30" : "bg-rf-cream text-rf-ink border-rf-amber/60"}`}
              >
                {Number.isInteger(t.stepIndex) && (
                  <span
                    data-testid={`timer-step-badge-${t.id}`}
                    className={`text-[10px] sm:text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                      done
                        ? "bg-white/25 text-white"
                        : "bg-rf-cream text-rf-orange border border-rf-orange/30"
                    }`}
                    title={`Started during step ${t.stepIndex + 1}`}
                  >
                    Step {t.stepIndex + 1}
                  </span>
                )}
                <span className="font-mono font-black text-xl sm:text-2xl tabular-nums">{fmt(t.remaining)}</span>
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wider max-w-[120px] truncate">{t.name || t.label}</span>
                {paused && (
                  <span data-testid={`timer-paused-${t.id}`} className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-rf-ink text-rf-cream shrink-0">Paused</span>
                )}
                <button onClick={() => removeTimer(t.id)} className={`ml-1 text-base font-bold ${done ? "text-white/80" : "text-rf-ink-soft"} hover:opacity-70 leading-none`} aria-label="Dismiss timer">✕</button>
              </div>
            );
          })}
        </div>
      )}

      {khAnswer && (
        <div
          role="status"
          aria-live="polite"
          data-testid="kitchen-kh-answer"
          className="fixed left-1/2 -translate-x-1/2 bottom-28 z-40 w-[min(92vw,560px)] bg-rf-cream text-rf-ink rounded-2xl shadow-2xl p-5 border-2 border-rf-amber"
        >
          <button onClick={() => setKhAnswer(null)} aria-label="Close answer" data-testid="kitchen-kh-answer-close" className="absolute top-2 right-3 text-rf-ink-soft hover:text-rf-ink font-bold">✕</button>
          <p className="text-[10px] uppercase tracking-widest font-black text-rf-orange mb-1">Kitchen Help</p>
          <p className="font-display text-xl font-black mb-1" data-testid="kitchen-kh-answer-term">{khAnswer.term}</p>
          <p className="text-sm leading-relaxed" data-testid="kitchen-kh-answer-def">
            {khAnswer.definition || "Not in Kitchen Help yet — we've noted it so the team can add it."}
          </p>
        </div>
      )}

      {/* Help overlay */}
      {helpOpen && (
        <div
          data-testid="kitchen-help-overlay"
          className="fixed inset-0 z-50 bg-rf-ink/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={(e) => { e.stopPropagation(); setHelpOpen(false); }}
        >
          <div className="bg-rf-cream rounded-3xl text-rf-ink relative w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button data-testid="kitchen-help-close" onClick={() => setHelpOpen(false)} className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-rf-cream-alt hover:bg-rf-amber font-bold text-lg shadow-sm" aria-label="Close help">✕</button>

            <div className="overflow-y-auto p-6 sm:p-8 pb-2">
              <p className="overline mb-3 text-rf-orange">Kitchen View · how to use</p>
              <h2 className="font-display text-3xl font-black mb-5 leading-tight">Cook hands-free.</h2>

              <div className="space-y-5 text-sm sm:text-base">
                <div>
                  <p className="font-display font-extrabold text-lg mb-1"><i className="fa-solid fa-hand-pointer text-rf-orange mr-1.5" /> Tap to navigate</p>
                  <p className="text-rf-ink-soft">Tap the <b>right half</b> of the screen for the next step, the <b>left half</b> to go back. Or use the buttons at the bottom.</p>
                </div>

                <div>
                  <p className="font-display font-extrabold text-lg mb-1"><i className="fa-solid fa-microphone text-rf-orange mr-1.5" /> Voice — say these out loud</p>
                  <ul className="text-rf-ink-soft space-y-1 ml-4 list-disc">
                    <li><b>Step by step:</b> "next", "back", "repeat"</li>
                    <li><b>Jump anywhere:</b> "go to step 3", "jump to step five", "go back to step 2", "go forward to step 7"</li>
                    <li><b>Start a timer:</b> "set timer for 10 minutes" — or name it: "<i>10 minute timer called pasta</i>"</li>
                    <li><b>Pause / resume:</b> "pause timer", "pause step 3 timer", "resume timer", "restart step 2 timer"</li>
                    <li><b>Cancel:</b> "cancel timer", "cancel step 3 timer", "clear all timers"</li>
                    <li><b>Change length:</b> "change timer to 15 minutes", "set step 3 timer to 5 minutes"</li>
                    <li><b>Check remaining:</b> "how long left?", "time left on the pasta timer?"</li>
                    <li><b>Toggle reading:</b> "read on" / "read off"</li>
                    <li><b>Open / close this guide:</b> "help" / "close help"</li>
                    <li><b>Ask a cooking question:</b> "what is arrowroot?" / "what does fold mean?" — hear the Kitchen Help answer without leaving the recipe ("close answer" to hide)</li>
                    <li><b>Ingredients panel:</b> "show ingredients" / "show me the ingredients" / "list ingredients" / "what are the ingredients" — close with "close ingredients", "done", or "back to cooking"</li>
                    <li><b>Scroll ingredients hands-free:</b> once open, say "scroll down" / "scroll up" to walk the list, or "scroll to top" / "bottom of list" to jump straight to either end</li>
                    <li><b>Wake screen:</b> when the screen dims, just speak any command to resume</li>
                    <li><b>Turn voice off:</b> "voice off" (turn back on with the top-bar button)</li>
                    <li><b>Leave:</b> "exit"</li>
                  </ul>
                  <p className="text-xs text-rf-ink-soft italic mt-2">A small chime plays when a voice command is acknowledged. Numbers can be spoken as words too — "step three" works the same as "step 3".</p>
                </div>

                <div>
                  <p className="font-display font-extrabold text-lg mb-1"><i className="fa-solid fa-volume-high text-rf-orange mr-1.5" /> Read — text-to-speech</p>
                  <p className="text-rf-ink-soft">Kitchen View reads step one aloud as soon as it opens, then each step as you advance. Tap "Read ON" (or say "stop reading") to turn it off. The voice is a British English woman by default.</p>
                  <div className="mt-3 p-3 rounded-xl bg-rf-cream-alt">
                    <VoicePicker compact />
                  </div>
                </div>

                <div>
                  <p className="font-display font-extrabold text-lg mb-1"><i className="fa-solid fa-clock text-rf-orange mr-1.5" /> Smart timers</p>
                  <p className="text-rf-ink-soft">Any time phrase in the step (e.g. <i>"simmer for 10 minutes"</i>) becomes a tappable button below the text. You can also start, pause, resume, change or cancel timers hands-free with your voice — and give them a name like "pasta" or "sauce". When a timer is done you'll hear a chime and a spoken alert that says <i>which</i> timer just finished. Paused timers stay visible with a <b>Paused</b> badge until you resume them.</p>
                </div>

                <div>
                  <p className="font-display font-extrabold text-lg mb-1"><i className="fa-solid fa-moon text-rf-orange mr-1.5" /> Ambient mode</p>
                  <p className="text-rf-ink-soft">After 60 seconds of no activity the screen dims into a soft screensaver to save battery. <b>Tap or just speak any command</b> — "next", "back", "repeat", or a timer word — to instantly resume.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-rf-ink/10 bg-rf-cream p-4 sm:px-8">
              <button
                data-testid="kitchen-help-got-it"
                onClick={() => setHelpOpen(false)}
                className="btn-ink w-full"
              >
                Got it · Start cooking
              </button>
              <p className="text-center text-xs text-rf-ink-soft mt-2">Press <kbd className="px-1.5 py-0.5 rounded bg-rf-cream-alt font-mono text-[10px]">Esc</kbd> or tap outside to dismiss.</p>
            </div>
          </div>
        </div>
      )}

      {/* Ingredients help overlay — tap ? beside an ingredient to hear its definition */}
      {ingHelpOpen && (
        <div
          data-testid="kitchen-ing-overlay"
          className="fixed inset-0 z-50 bg-rf-ink/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={(e) => { e.stopPropagation(); setIngHelpOpen(false); }}
          role="dialog"
          aria-modal="true"
          aria-label="Recipe ingredients"
        >
          <div
            ref={ingTrapRef}
            className="bg-rf-cream rounded-3xl text-rf-ink relative w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="kitchen-ing-close" onClick={() => setIngHelpOpen(false)} className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-rf-cream-alt hover:bg-rf-amber font-bold text-lg shadow-sm" aria-label="Close ingredients">✕</button>
            <div className="overflow-y-auto p-6 sm:p-8" ref={ingScrollRef} data-testid="kitchen-ing-scroll">
              <p className="overline mb-2 text-rf-orange">Ingredients · tap ? to hear the meaning</p>
              <h2 className="font-display text-3xl font-black mb-5 leading-tight">What&apos;s in this dish?</h2>
              {/* Voice-close hint — only when voice is actually running so
                  we don't lie about mic availability. Explains the exact
                  phrases that work so cooks don't guess. */}
              {voiceOn && voiceActuallyListening && (
                <div
                  data-testid="kitchen-ing-voice-hint"
                  className="mb-4 flex items-start gap-2 rounded-xl bg-rf-amber/15 border border-rf-amber/40 px-3 py-2 text-xs text-rf-ink"
                >
                  <i className="fa-solid fa-microphone text-rf-orange mt-0.5" />
                  <span>
                    Hands full? Say <b>&ldquo;scroll down&rdquo;</b> / <b>&ldquo;scroll up&rdquo;</b> to walk the list, <b>&ldquo;scroll to top&rdquo;</b> or <b>&ldquo;bottom of list&rdquo;</b> to jump, or <b>&ldquo;done&rdquo;</b> / <b>&ldquo;close ingredients&rdquo;</b> to dismiss.
                  </span>
                </div>
              )}
              {(!recipe.ingredients || recipe.ingredients.length === 0) && (
                <p className="text-rf-ink-soft italic">No ingredients recorded on this recipe.</p>
              )}
              <ul className="space-y-2">
                {(recipe.ingredients || []).map((ing, i) => {
                  const nameLower = (ing.name || "").toLowerCase().trim();
                  let match = null;
                  if (khIndex && khIndex.termsByLower) {
                    // Longest-match — same rule as the recipe page auto-linker
                    for (const key of khIndex.sortedKeys) {
                      if (new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(nameLower)) {
                        match = khIndex.termsByLower.get(key);
                        break;
                      }
                    }
                  }
                  return (
                    <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-rf-ink/5" data-testid={`kitchen-ing-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-rf-ink">
                          {ing.quantity ? <span className="text-rf-orange">{ing.quantity} {ing.unit} </span> : null}
                          {ing.name}
                        </p>
                        {ing.notes && <p className="text-xs text-rf-ink-soft mt-0.5">{ing.notes}</p>}
                      </div>
                      {match ? (
                        <button
                          data-testid={`kitchen-ing-explain-${i}`}
                          onClick={async () => {
                            try {
                              const r = await api.get(`/public/kitchen-help/${match.slug}`);
                              const defn = r.data.definition || match.term;
                              // Speak aloud + reveal below
                              try { speak(`${match.term}. ${defn}`); } catch { /* voice may be blocked */ }
                              // Toggle expanded definition below the row
                              const el = document.getElementById(`kitchen-ing-def-${i}`);
                              if (el) {
                                if (el.dataset.open === "1") {
                                  el.dataset.open = "0";
                                  el.style.display = "none";
                                } else {
                                  el.textContent = defn;
                                  el.dataset.open = "1";
                                  el.style.display = "block";
                                }
                              }
                            } catch { /* silent */ }
                          }}
                          className="shrink-0 w-9 h-9 rounded-full bg-rf-amber text-rf-ink font-black hover:bg-rf-orange hover:text-white transition"
                          title={`Explain ${match.term}`}
                          aria-label={`Explain ${match.term}`}
                        >
                          ?
                        </button>
                      ) : (
                        <span className="shrink-0 w-9 h-9 rounded-full bg-rf-ink/5 text-rf-ink/25 inline-flex items-center justify-center text-xs" title="No glossary entry">—</span>
                      )}
                      <p id={`kitchen-ing-def-${i}`} data-open="0" style={{ display: "none" }} className="basis-full text-sm text-rf-ink-soft leading-relaxed pt-2 border-t border-rf-ink/10 mt-2" />
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-xs text-rf-ink-soft italic">
                Definitions come from <a href="/kitchen-help" className="underline">Kitchen Help</a> — Recipe Filer&apos;s culinary glossary.
              </p>
            </div>
            <div className="border-t border-rf-ink/10 bg-rf-cream p-4 sm:px-8">
              <button
                data-testid="kitchen-ing-done"
                onClick={() => setIngHelpOpen(false)}
                className="btn-ink w-full"
              >
                Back to cooking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ambient dim overlay */}
      {ambient && !helpOpen && (
        <div
          data-testid="kitchen-ambient"
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-rf-cream cursor-pointer"
          onClick={(e) => { e.stopPropagation(); setAmbient(false); lastActivityRef.current = Date.now(); }}
        >
          <p className="overline text-rf-amber mb-4">Ambient mode</p>
          <p className="font-display text-3xl md:text-5xl font-black mb-3 text-center px-6">{recipe.title}</p>
          <p className="text-rf-cream/60 text-lg">Step {step + 1} of {total}</p>
          <p className="text-rf-cream/40 text-sm mt-10">Tap the screen or say <b>&ldquo;next&rdquo;</b>, <b>&ldquo;back&rdquo;</b>, or <b>&ldquo;repeat&rdquo;</b> to resume</p>
        </div>
      )}
    </div>
  );
}
